import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

/**
 * ConfigTab - Reorder items and configure resume settings
 * 
 * Shows:
 * - List of selected items with reorder controls
 * - Template selector
 * - Page target setting
 */
const ConfigTab = ({
  selectedItems,
  resumeConfig,
  onUpdateConfig,
  onToggleSelection
}) => {
  // Reordering removed - can be added later if needed
  // For now, items shown in selection order (managed by parent)

  // Handle template change
  const handleTemplateChange = (e) => {
    onUpdateConfig({ ...resumeConfig, template: e.target.value });
  };

  // Handle target pages change
  const handleTargetPagesChange = (e) => {
    onUpdateConfig({ ...resumeConfig, targetPages: parseInt(e.target.value) });
  };

  return (
    <Box>
      {/* Settings Section */}
      <Box mb={3}>
        <Typography variant="subtitle2" gutterBottom>
          Resume Settings
        </Typography>
        
        {/* Template Selector */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Template</InputLabel>
          <Select
            value={resumeConfig?.template || 'modern'}
            onChange={handleTemplateChange}
            label="Template"
          >
            <MenuItem value="professional">Professional</MenuItem>
            <MenuItem value="modern">Modern</MenuItem>
            <MenuItem value="minimal">Minimal</MenuItem>
          </Select>
        </FormControl>

        {/* Target Pages */}
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          Target Length:
        </Typography>
        <RadioGroup
          value={resumeConfig?.targetPages || 2}
          onChange={handleTargetPagesChange}
          row
        >
          <FormControlLabel value={1} control={<Radio size="small" />} label="1 page" />
          <FormControlLabel value={2} control={<Radio size="small" />} label="2 pages" />
        </RadioGroup>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Selected Items List */}
      <Typography variant="subtitle2" gutterBottom>
        Selected Items ({selectedItems.length})
      </Typography>

      {selectedItems.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            No items selected yet.
            <br />
            Add items from the Profile tab.
          </Typography>
        </Box>
      ) : (
        <Box>
          {selectedItems.map((item, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                {/* Item number */}
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 24 }}>
                  #{index + 1}
                </Typography>
                
                {/* Item Info */}
                <Box flex={1}>
                  <Typography variant="caption" fontWeight="medium" display="block">
                    {item.type}
                  </Typography>
                  {item.company && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.company} - {item.title}
                    </Typography>
                  )}
                  {item.name && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.name}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {item.text || item.description}
                  </Typography>
                </Box>
                
                {/* Note: Reordering and deletion removed - manage via Profile/Criteria tabs */}
              </Stack>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ConfigTab;
